import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';

/**
 * Utility function to compress and convert an image file to a base64 Data URL.
 * Automatically resizes large images to max 1600px width/height while preserving quality.
 */
export const processImageFile = (file, maxWidth = 1600, maxHeight = 1600, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    if (!file.type.startsWith('image/')) {
      reject(new Error('Selected file must be an image (PNG, JPG, JPEG, WEBP)'));
      return;
    }

    const maxSizeBytes = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSizeBytes) {
      reject(new Error('File size exceeds 5MB limit. Please choose a smaller image.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Resize if dimensions exceed max limits
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to high-quality JPEG Data URL (or PNG if original was PNG with alpha)
        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(mimeType, quality);
        resolve(dataUrl);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image file.'));
      };
      img.src = readerEvent.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file.'));
    };
    reader.readAsDataURL(file);
  });
};

const DocumentUploadField = ({
  label,
  name,
  value,
  onChange,
  imageValue,
  onImageChange,
  required = false,
  imageRequired = false,
  placeholder = '',
  helpText = '',
  documentTitle = 'Document',
  onPreviewImage
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;

    try {
      setIsProcessing(true);
      const base64Data = await processImageFile(file);
      onImageChange(base64Data);
      toast.success(`${documentTitle} image attached!`);
    } catch (err) {
      console.error('Image processing error:', err);
      toast.error(err.message || 'Failed to process image');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-gray-50/70 border border-gray-200 rounded-xl p-4 transition hover:border-blue-300">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {/* Left Side: Text Input for Document Number */}
        <div className="flex-1">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 font-medium"
          />
          {helpText && (
            <p className="mt-1 text-xs text-gray-500">{helpText}</p>
          )}
        </div>

        {/* Right Side: Upload Image Box Beside the Input */}
        <div className="w-full md:w-56 flex-shrink-0">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center justify-between">
            <span>Upload {documentTitle} Image {(imageRequired || required) && <span className="text-red-500">*</span>}</span>
            {imageValue ? (
              <span className="text-[10px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded-full flex items-center">
                <i className="fas fa-check-circle mr-1"></i> Attached
              </span>
            ) : (
              <span className="text-[10px] text-gray-400 font-normal">JPG, PNG, WEBP</span>
            )}
          </label>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg, image/jpg, image/webp"
            onChange={handleInputChange}
            className="hidden"
          />

          {imageValue ? (
            /* Uploaded State Preview */
            <div className="relative group border border-blue-200 bg-blue-50/50 rounded-lg p-2 flex items-center space-x-3 transition hover:shadow-sm">
              <div 
                className="w-14 h-14 rounded-md overflow-hidden bg-white border border-gray-200 cursor-pointer flex-shrink-0 flex items-center justify-center relative group-hover:opacity-90"
                onClick={() => onPreviewImage && onPreviewImage(imageValue, `${documentTitle} Image`)}
                title="Click to view full image"
              >
                <img
                  src={imageValue}
                  alt={documentTitle}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition">
                  <i className="fas fa-search-plus"></i>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {documentTitle} Attached
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <button
                    type="button"
                    onClick={() => onPreviewImage && onPreviewImage(imageValue, `${documentTitle} Image`)}
                    className="text-[11px] font-medium text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <i className="fas fa-eye mr-1"></i> View
                  </button>
                  <span className="text-gray-300">•</span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-[11px] font-medium text-gray-600 hover:text-gray-900 flex items-center"
                  >
                    <i className="fas fa-sync-alt mr-1"></i> Change
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleRemoveImage}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition"
                title="Remove image"
              >
                <i className="fas fa-trash-alt text-xs"></i>
              </button>
            </div>
          ) : (
            /* Upload Drop Area */
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-2.5 flex flex-col items-center justify-center text-center cursor-pointer transition ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30 bg-white'
              }`}
            >
              {isProcessing ? (
                <div className="py-2 flex items-center space-x-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-xs font-medium">Processing...</span>
                </div>
              ) : (
                <div className="py-1">
                  <div className="flex items-center justify-center space-x-1.5 text-blue-600 mb-0.5">
                    <i className="fas fa-cloud-upload-alt text-sm"></i>
                    <span className="text-xs font-semibold">Choose File</span>
                  </div>
                  <p className="text-[10px] text-gray-500">or drag & drop here</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadField;
